import {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
} from "react";
import { uploadKbDocuments, type KbUploadFileResult } from "@service/knowledge-base";
import { catchErrorLog } from "@util/sentry";

export type KbDocFileStatus = "uploading" | "done" | "failed";
export type KbUploadPhase = "uploading" | "done";

export interface KbDocFile {
    id: string;
    filename: string;
    sizeBytes: number;
    status: KbDocFileStatus;
    /** BE error text for a failed file, surfaced in the toast. */
    errorMessage?: string;
}

export interface KbUploadState {
    files: KbDocFile[];
    phase: KbUploadPhase;
    uploadProgress: number;
}

interface KbUploadContextValue {
    uploadState: KbUploadState | null;
    isToastVisible: boolean;
    isProcessing: boolean;
    toastFlashCount: number;
    /** Increments each time an upload batch finishes (success or failure), so
     *  the KB page can refetch the document list/uploaders/stats. */
    uploadCompletedCount: number;
    beginUpload: (files: File[]) => void;
    retryFile: (id: string) => void;
    removeFile: (id: string) => void;
    dismissToast: () => void;
    flashToast: () => void;
}

const KbUploadContext = createContext<KbUploadContextValue | null>(null);

function makeFileId(file: File, index: number): string {
    return `${file.name}:${file.size}:${file.lastModified}:${index}`;
}

function allTerminal(files: KbDocFile[]): KbUploadPhase {
    return files.every((f) => f.status === "done" || f.status === "failed")
        ? "done"
        : "uploading";
}

export function KbUploadProvider({ children }: { children: React.ReactNode }) {
    const [uploadState, setUploadState] = useState<KbUploadState | null>(null);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastFlashCount, setToastFlashCount] = useState(0);
    const [uploadCompletedCount, setUploadCompletedCount] = useState(0);
    const originalFilesRef = useRef<Map<string, File>>(new Map());

    const updateProgress = useCallback((pct: number) => {
        setUploadState((prev) => (prev ? { ...prev, uploadProgress: pct } : prev));
    }, []);

    // Correlate the BE's per-file results back to the toast rows and flip each
    // to done/failed. When the result count matches the files sent, we zip by
    // order (BE preserves request order); otherwise we fall back to matching
    // failed results by filename and treat the rest as done.
    const applyResults = useCallback(
        (entryIds: string[], results: KbUploadFileResult[]) => {
            setUploadState((prev) => {
                if (!prev) return prev;
                const byId = new Map(prev.files.map((f) => [f.id, { ...f }]));
                const setFromResult = (id: string, r: KbUploadFileResult) => {
                    const f = byId.get(id);
                    if (!f) return;
                    f.status = r.status === "failed" ? "failed" : "done";
                    f.errorMessage = r.status === "failed" ? r.error : undefined;
                };
                if (results.length === entryIds.length) {
                    entryIds.forEach((id, i) => setFromResult(id, results[i]));
                } else {
                    const failedNames = new Set(
                        results.filter((r) => r.status === "failed").map((r) => r.filename),
                    );
                    entryIds.forEach((id) => {
                        const f = byId.get(id);
                        if (!f) return;
                        f.status = failedNames.has(f.filename) ? "failed" : "done";
                    });
                }
                const files = Array.from(byId.values());
                return { ...prev, files, phase: allTerminal(files), uploadProgress: 100 };
            });
        },
        [],
    );

    // Mark every still-uploading row failed — used when the whole request is
    // rejected (network error, 413 over-limit, 415 bad type, 403 not allowed).
    const failPending = useCallback((message: string) => {
        setUploadState((prev) => {
            if (!prev) return prev;
            const files = prev.files.map((f) =>
                f.status === "uploading" ? { ...f, status: "failed" as KbDocFileStatus, errorMessage: message } : f,
            );
            return { ...prev, files, phase: allTerminal(files) };
        });
    }, []);

    const runUpload = useCallback(
        async (entries: { id: string; file: File }[]) => {
            try {
                const { results } = await uploadKbDocuments(
                    entries.map((e) => e.file),
                    updateProgress,
                );
                applyResults(entries.map((e) => e.id), results);
            } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                catchErrorLog(`[kb] document upload failed: ${msg}`);
                failPending("Upload failed. Retry.");
            } finally {
                setUploadCompletedCount((c) => c + 1);
            }
        },
        [updateProgress, applyResults, failPending],
    );

    const beginUpload = useCallback(
        (files: File[]) => {
            const map = new Map<string, File>();
            const entries: { id: string; file: File }[] = [];
            const docFiles: KbDocFile[] = files.map((f, i) => {
                const id = makeFileId(f, i);
                map.set(id, f);
                entries.push({ id, file: f });
                return { id, filename: f.name, sizeBytes: f.size, status: "uploading" };
            });
            originalFilesRef.current = map;
            setUploadState({ files: docFiles, phase: "uploading", uploadProgress: 0 });
            setIsToastVisible(true);
            void runUpload(entries);
        },
        [runUpload],
    );

    const retryFile = useCallback(
        (id: string) => {
            const file = originalFilesRef.current.get(id);
            setUploadState((prev) => {
                if (!prev) return prev;
                const files = prev.files.map((f) =>
                    f.id === id ? { ...f, status: "uploading" as KbDocFileStatus, errorMessage: undefined } : f,
                );
                return { ...prev, files, phase: "uploading", uploadProgress: 0 };
            });
            if (!file) {
                // No original File to resend (e.g. after a refresh) — can't retry.
                failPending("Cannot retry — re-add the file.");
                return;
            }
            void runUpload([{ id, file }]);
        },
        [runUpload, failPending],
    );

    const removeFile = useCallback((id: string) => {
        setUploadState((prev) => {
            if (!prev) return prev;
            const files = prev.files.filter((f) => f.id !== id);
            return { ...prev, files, phase: allTerminal(files) };
        });
    }, []);

    const dismissToast = useCallback(() => {
        setIsToastVisible(false);
        setUploadState(null);
        originalFilesRef.current.clear();
    }, []);

    const flashToast = useCallback(() => {
        setToastFlashCount((c) => c + 1);
    }, []);

    const isProcessing =
        uploadState !== null && uploadState.phase === "uploading";

    return (
        <KbUploadContext.Provider
            value={{
                uploadState,
                isToastVisible,
                isProcessing,
                toastFlashCount,
                uploadCompletedCount,
                beginUpload,
                retryFile,
                removeFile,
                dismissToast,
                flashToast,
            }}
        >
            {children}
        </KbUploadContext.Provider>
    );
}

export function useKbUpload(): KbUploadContextValue {
    const ctx = useContext(KbUploadContext);
    if (!ctx) throw new Error("useKbUpload must be used within KbUploadProvider");
    return ctx;
}
